// src/layouts/HeaderAttendanceButton.jsx
// Chấm công nhanh trên header: hiện trạng thái hôm nay, check-in / checkout ngay tại chỗ.

import { useCallback, useEffect, useState } from "react";
import { Fingerprint, LogOut, Loader2 } from "lucide-react";
import {
  getAttendanceStatus,
  getActiveCheckins,
  checkoutAttendance,
} from "../api/hrm/employees/attendanceLog";
import { hasPermission } from "../utils/permissionUtils";
import { showSuccessToast, showErrorToast } from "../components/ConfirmDialog";
import AttendanceLogCreateModal from "../modules/hrm/attendance/AttendanceLogCreateModal";
import CheckoutConfirmModal from "../modules/hrm/attendance/CheckoutConfirmModal";

const isToday = (value) =>
  new Date(value).toLocaleDateString("en-CA") === new Date().toLocaleDateString("en-CA");

export default function HeaderAttendanceButton({ user }) {
  const canUseAttendance = hasPermission("hrm.attendance", "R");

  const [status, setStatus] = useState(null);
  const [activeLog, setActiveLog] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [processing, setProcessing] = useState(false);

  const refresh = useCallback(async () => {
    if (!canUseAttendance) return;
    try {
      const [statusRes, activeRes] = await Promise.all([
        getAttendanceStatus().catch(() => null),
        getActiveCheckins().catch(() => []),
      ]);
      setStatus(statusRes);
      setActiveLog(
        (activeRes || []).find(
          (log) => log.employee_id === user?.id && isToday(log.work_date)
        ) || null
      );
    } catch (err) {
      console.error("❌ Lỗi lấy trạng thái chấm công trên header:", err);
    }
  }, [canUseAttendance, user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!canUseAttendance) return null;

  const handleCheckout = async (data) => {
    try {
      setProcessing(true);
      await checkoutAttendance(activeLog.id, data.check_out_location);
      showSuccessToast("Checkout thành công!");
      setShowCheckout(false);
      await refresh();
    } catch (err) {
      showErrorToast("Không thể checkout");
    } finally {
      setProcessing(false);
    }
  };

  const isWorking = Boolean(activeLog);
  const canCheckin = status?.can_checkin;

  return (
    <>
      <button
        onClick={() => (isWorking ? setShowCheckout(true) : setShowCheckin(true))}
        title={
          isWorking
            ? `Đang làm việc từ ${activeLog.check_in_time} — bấm để checkout`
            : status?.message || "Chấm công"
        }
        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-bold transition-colors ${
          isWorking
            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : canCheckin
              ? "bg-red-700 text-white shadow-md shadow-red-100 hover:bg-red-800"
              : "bg-gray-50 text-gray-500 hover:bg-gray-100"
        }`}
      >
        {processing ? (
          <Loader2 size={15} className="animate-spin" />
        ) : isWorking ? (
          <LogOut size={15} />
        ) : (
          <Fingerprint size={15} />
        )}
        <span className="hidden lg:inline">
          {isWorking
            ? `Checkout · ${String(activeLog.check_in_time).slice(0, 5)}`
            : "Chấm công"}
        </span>
      </button>

      <AttendanceLogCreateModal
        isOpen={showCheckin}
        onClose={() => setShowCheckin(false)}
        onSaved={refresh}
      />

      {showCheckout && activeLog && (
        <CheckoutConfirmModal
          isOpen={showCheckout}
          checkInTime={activeLog.check_in_time}
          workDate={activeLog.work_date}
          onClose={() => setShowCheckout(false)}
          onConfirm={handleCheckout}
        />
      )}
    </>
  );
}
